import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const CartContext = createContext(null);
const KEY = "sync_cart";

function loadStoredCart() {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY));
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((i) =>
        i && typeof i === "object" &&
        typeof i.key === "string" && typeof i.product_id === "string" &&
        typeof i.name === "string" && typeof i.image === "string" &&
        typeof i.price === "number" && Number.isFinite(i.price) && i.price >= 0 &&
        Number.isInteger(i.quantity) && i.quantity > 0)
      .map((i) => ({
        key: i.key, product_id: i.product_id, name: i.name, price: i.price,
        image: i.image, size: i.size ?? null, color: i.color ?? null,
        quantity: Math.min(i.quantity, 99),
      }));
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadStoredCart);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);

  useEffect(() => {
    if (!user) return;
    const t = setTimeout(() => {
      api.post("/cart/sync", { items: items.map(({ key, ...rest }) => rest) })
        .catch((e) => console.warn("Cart sync failed:", e.message));
    }, 1200);
    return () => clearTimeout(t);
  }, [items, user]);

  const addItem = useCallback((product, size, color) => {
    const key = `${product.id}-${size}-${color}`;
    setItems((prev) => {
      const found = prev.find((i) => i.key === key);
      if (found) return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + 1 } : i));
      return [...prev, {
        key, product_id: product.id, name: product.name, price: product.price,
        image: (product.images_by_color?.[color] || product.images)[0], size, color, quantity: 1,
      }];
    });
    setOpen(true);
  }, []);

  const updateQty = useCallback((key, delta) =>
    setItems((prev) => prev
      .map((i) => (i.key === key ? { ...i, quantity: Math.max(1, i.quantity + delta) } : i))), []);

  const removeItem = useCallback((key) => setItems((prev) => prev.filter((i) => i.key !== key)), []);
  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(() => ({
    items, addItem, updateQty, removeItem, clear, open, setOpen,
    count: items.reduce((s, i) => s + i.quantity, 0),
    total: items.reduce((s, i) => s + i.price * i.quantity, 0),
  }), [items, addItem, updateQty, removeItem, clear, open]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
